import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import MobileBottomNav from './MobileBottomNav';
import ModalPensamiento from '../sala/ModalPensamiento';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa: '#5dc1b9', mango: '#FDBE02', morado: '#534AB7', moradoClaro: '#AFA9EC' };

function formatearTiempo(fecha) {
  if (!fecha) return '';
  const diff = Date.now() - new Date(fecha).getTime();
  const min = Math.floor(diff / 60000);
  const hr = Math.floor(diff / 3600000);
  const dia = Math.floor(diff / 86400000);
  if (min < 1) return 'ahora';
  if (min < 60) return `${min}m`;
  if (hr < 24) return `${hr}h`;
  return `${dia}d`;
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/** Extract wikilink titles from HTML content */
function extractWikilinks(html) {
  if (!html) return [];
  const links = [];
  // Match <span class="wikilink"...>[[Title]]</span> or plain [[Title]]
  const spanRegex = /data-pensamiento-id="([^"]+)"[^>]*>\[\[([^\]]+)\]\]/g;
  let m;
  while ((m = spanRegex.exec(html))) links.push({ id: m[1], title: m[2] });
  // Also match plain [[Title]] not in spans
  const plainRegex = /\[\[([^\]]+)\]\]/g;
  while ((m = plainRegex.exec(html))) {
    if (!links.find(l => l.title === m[1])) links.push({ id: null, title: m[1] });
  }
  return links;
}

/** Build sinapsis map: pensamiento_id → array of connected pensamiento_ids */
function buildSinapsisMap(sinapsis, pensamientos) {
  const map = {};
  const pensMap = {};
  (pensamientos || []).forEach(p => { pensMap[p.id] = p; });
  (sinapsis || []).forEach(s => {
    if (s.tipo !== 'wikilink') return;
    const fromId = s.from_id || s.pensamiento_from_id;
    const toId = s.to_id || s.pensamiento_to_id;
    if (!fromId || !toId || fromId === toId) return;
    if (!map[fromId]) map[fromId] = [];
    if (pensMap[toId]) map[fromId].push(pensMap[toId]);
  });
  return map;
}

export default function MobileFeedSala({ pensamientos, emprendedores, sinapsis, user, profile, membership, onNewPensamiento }) {
  const navigate = useNavigate();
  const [pensamientoSeleccionado, setPensamientoSeleccionado] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [contenido, setContenido] = useState('');
  const [guardando, setGuardando] = useState(false);
  const sinapsisMap = buildSinapsisMap(sinapsis, pensamientos);

  // Sort pensamientos by date (most recent first)
  const sorted = [...pensamientos].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Map user_id to profile
  const perfilMap = {};
  emprendedores.forEach(e => { perfilMap[e.user_id || e.id] = e; });

  const handlePublicar = async () => {
    if (!titulo.trim() || guardando) return;
    setGuardando(true);
    const { data: nuevo, error } = await supabase.from('pensamientos').insert({
      user_id: user.id, titulo: titulo.trim(), contenido: contenido.trim(),
      visibilidad: 'publico', tags: [],
    }).select().single();

    if (error) { alert('Error: ' + error.message); setGuardando(false); return; }

    // Create cluster sinapsis
    await supabase.from('sinapsis').insert({
      pensamiento_from_id: nuevo.id, user_from_id: user.id, user_to_id: user.id,
      tipo: 'cluster', score: 1.0,
    });

    setGuardando(false);
    setTitulo('');
    setContenido('');
    setEditorOpen(false);
    if (onNewPensamiento) onNewPensamiento(nuevo);
  };

  // Full-screen editor overlay for public thoughts
  if (editorOpen) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        height: '100dvh', maxHeight: '100dvh',
        background: '#0a0a0f', zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        fontFamily: font, color: '#fff',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '0.5px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
        }}>
          <button
            onClick={() => { setEditorOpen(false); setTitulo(''); setContenido(''); }}
            style={{
              background: 'rgba(93,193,185,0.15)', border: '0.5px solid rgba(93,193,185,0.3)',
              color: C.turquesa, padding: '6px 14px', borderRadius: 8,
              fontSize: 13, cursor: 'pointer', fontFamily: font, fontWeight: 500,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            ← Cancelar
          </button>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>🌐 Público</span>
          <button
            onClick={handlePublicar}
            disabled={guardando || !titulo.trim()}
            style={{
              background: titulo.trim() ? 'linear-gradient(135deg, #5dc1b9, #1D9E75)' : 'rgba(255,255,255,0.06)',
              border: 'none', color: titulo.trim() ? '#0a0a0f' : 'rgba(255,255,255,0.3)',
              padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: titulo.trim() ? 'pointer' : 'default', fontFamily: font,
              opacity: guardando ? 0.5 : 1,
            }}
          >
            {guardando ? '⏳...' : '🚀 Publicar'}
          </button>
        </header>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px', WebkitOverflowScrolling: 'touch', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="text"
            value={titulo}
            onChange={e => setTitulo(e.target.value)}
            placeholder="Título del pensamiento..."
            autoFocus
            style={{
              width: '100%', background: 'transparent', border: 'none',
              color: '#fff', fontFamily: serif, fontSize: 24, padding: '16px 0 12px',
              outline: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.1)',
              boxSizing: 'border-box',
            }}
          />

          <textarea
            value={contenido}
            onChange={e => setContenido(e.target.value)}
            placeholder="Escribe tu pensamiento...&#10;&#10;Tip: Usa [[nombre del pensamiento]] para conectar con otros pensamientos existentes."
            style={{
              width: '100%', minHeight: 200, background: 'transparent',
              border: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 15,
              lineHeight: 1.8, padding: '16px 0', outline: 'none', resize: 'none',
              fontFamily: font, boxSizing: 'border-box',
            }}
          />

          {/* Wikilink tip */}
          <div style={{
            marginTop: 12, padding: '12px 14px',
            background: 'rgba(93,193,185,0.06)',
            border: '0.5px solid rgba(93,193,185,0.15)',
            borderRadius: 10,
          }}>
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>
              💡 <strong style={{ color: C.turquesa }}>Conexiones:</strong> Escribe <code style={{ background: 'rgba(93,193,185,0.12)', color: C.turquesa, padding: '1px 5px', borderRadius: 3 }}>[[título]]</code> para enlazar con otro pensamiento existente.
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>
              Ejemplo: "Esta idea conecta con [[mi primera nota]] porque..."
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', fontFamily: font, color: '#fff', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 40,
        background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '0.5px solid rgba(255,255,255,0.05)',
      }}>
        <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{
            fontFamily: serif, fontSize: 22, margin: 0, fontWeight: 400,
            background: 'linear-gradient(90deg, #5dc1b9, #FDBE02, #eb636b)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            T&D
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {membership === 'free_observer' && (
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)' }}>FREE</span>
            )}
            <div
              onClick={() => navigate('/perfil')}
              style={{
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                background: 'linear-gradient(135deg, #534AB7, #1D9E75)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 600, color: '#fff',
              }}
            >
              {(profile?.full_name || 'U')[0]}
            </div>
          </div>
        </div>

        {/* Section label */}
        <div style={{ padding: '0 20px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: 'rgba(93,193,185,0.8)', letterSpacing: 1.5, textTransform: 'uppercase' }}>La Sala</span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>·</span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{pensamientos.length} pensamientos</span>
        </div>
      </div>

      {/* Feed */}
      <div>
        {sorted.map((p, i) => {
          const autor = perfilMap[p.user_id];
          const identidad = autor?.identidad;
          const esMio = p.user_id === user?.id;
          const contenido = stripHtml(p.contenido);

          return (
            <div
              key={p.id}
              onClick={() => setPensamientoSeleccionado(p.id)}
              style={{
                padding: '16px 20px',
                borderBottom: '0.5px solid rgba(255,255,255,0.04)',
                cursor: 'pointer',
                display: 'flex', gap: 12, alignItems: 'flex-start',
              }}
            >
              {/* Avatar */}
              <div style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                background: identidad === 'thinker'
                  ? 'linear-gradient(135deg, #FDBE02, #e8a800)'
                  : 'linear-gradient(135deg, #5dc1b9, #3da89f)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, color: '#0a0a0f',
                overflow: 'hidden',
              }}>
                {autor?.avatar_url
                  ? <img src={autor.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (autor?.full_name || '?')[0].toUpperCase()
                }
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Author row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
                      {autor?.business_name || autor?.full_name || 'Emprendedor'}
                    </span>
                    {esMio && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 50, background: 'rgba(93,193,185,0.15)', color: C.turquesa }}>tú</span>}
                    <span style={{
                      fontSize: 9, padding: '1px 6px', borderRadius: 50,
                      background: identidad === 'thinker' ? 'rgba(253,190,2,0.12)' : 'rgba(93,193,185,0.12)',
                      color: identidad === 'thinker' ? C.mango : C.turquesa,
                    }}>
                      {identidad === 'thinker' ? '💡' : '⚡'} {identidad || 'doer'}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>
                    {formatearTiempo(p.created_at)}
                  </span>
                </div>

                {/* Business type */}
                {autor?.business_type && (
                  <p style={{ margin: '0 0 6px', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                    {autor.business_type}
                  </p>
                )}

                {/* Title */}
                {p.titulo && (
                  <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, fontFamily: serif, color: 'rgba(255,255,255,0.95)' }}>
                    {p.titulo}
                  </p>
                )}

                {/* Body preview */}
                <p style={{
                  margin: '0 0 8px', fontSize: 14, color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {contenido.substring(0, 180)}{contenido.length > 180 ? '...' : ''}
                </p>

                {/* Tags */}
                {p.tags && p.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {p.tags.slice(0, 3).map(tag => (
                      <span key={tag} style={{
                        fontSize: 11, padding: '2px 10px', borderRadius: 50,
                        background: 'rgba(93,193,185,0.1)', color: C.turquesa, fontWeight: 500,
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Wikilinks from content */}
                {(() => {
                  const wikilinks = extractWikilinks(p.contenido);
                  if (wikilinks.length === 0) return null;
                  return (
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>↗ conectado:</span>
                      {wikilinks.map((wl, wi) => (
                        <span key={wi} onClick={(e) => {
                          e.stopPropagation();
                          if (wl.id) setPensamientoSeleccionado(wl.id);
                        }} style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 4,
                          background: 'rgba(253,190,2,0.08)', border: '1px solid rgba(253,190,2,0.25)',
                          color: C.mango, cursor: wl.id ? 'pointer' : 'default', fontWeight: 500,
                        }}>
                          [[{wl.title}]]
                        </span>
                      ))}
                    </div>
                  );
                })()}

                {/* Sinapsis connections */}
                {(() => {
                  const connected = sinapsisMap[p.id];
                  if (!connected || connected.length === 0) return null;
                  return (
                    <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>⟶ sinapsis:</span>
                      {connected.slice(0, 3).map((c) => (
                        <span key={c.id} onClick={(e) => {
                          e.stopPropagation();
                          setPensamientoSeleccionado(c.id);
                        }} style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 4,
                          background: 'rgba(93,193,185,0.08)', border: '1px solid rgba(93,193,185,0.2)',
                          color: C.turquesa, cursor: 'pointer', fontWeight: 500,
                        }}>
                          {c.titulo || 'Sin título'}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}

        {sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <p style={{ fontSize: 40, margin: '0 0 12px' }}>🧠</p>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.4)' }}>La Sala está esperando pensamientos</p>
          </div>
        )}
      </div>

      {/* FAB - new public thought */}
      <button
        onClick={() => setEditorOpen(true)}
        style={{
          position: 'fixed', bottom: 90, right: 20, zIndex: 51,
          width: 56, height: 56, borderRadius: '50%', border: 'none',
          background: 'linear-gradient(135deg, #5dc1b9, #1D9E75)',
          color: '#0a0a0f', fontSize: 26, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 4px 24px rgba(93,193,185,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        +
      </button>

      {/* Bottom Nav */}
      <MobileBottomNav active="sala" navigate={navigate} />

      {/* Modal pensamiento */}
      {pensamientoSeleccionado && (
        <ModalPensamiento
          pensamientoId={pensamientoSeleccionado}
          currentUserId={user?.id}
          membership={membership}
          onCerrar={() => setPensamientoSeleccionado(null)}
        />
      )}
    </div>
  );
}
